import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { WorkflowRuleFullPage } from "@/components/Organisms/WorkflowRuleFullPage/WorkflowRuleFullPage";
import { EMPTY_ORG_ID } from "@/constants/org";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import { getRecordTypeDisplayName } from "@/utils/record-type-detail/record-type-detail.utils";
import { RecordTypeDrawerContext } from "@/utils/record-type-detail/record-type-drawer-context";

const SearchSchema = z.object({
	ruleTab: z.enum(["details", "activity"]).optional().default("details"),
});

const ROUTE_ID =
	"/_authenticated/(admin-app)/_admin/admin/configuration/recordTypes/$recordTypeId/full/rules/$ruleId" as const;

export const Route = createFileRoute(ROUTE_ID)({
	component: AdminRecordTypeRuleDetailPage,
	validateSearch: (search) => SearchSchema.parse(search),
});

function AdminRecordTypeRuleDetailPage() {
	const ctx = useContext(RecordTypeDrawerContext);
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({ from: ROUTE_ID });
	const search = useSearch({ from: ROUTE_ID });

	const canEditRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "EditWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canEditRule = Boolean(canEditRuleQuery.data);

	const canDeleteRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "DeleteWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canDeleteRule = Boolean(canDeleteRuleQuery.data);

	const canArchiveRuleQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ArchiveWorkflowRule",
			entityType: "WorkflowRule",
			entityId: params.ruleId,
		}),
	);
	const canArchiveRule = Boolean(canArchiveRuleQuery.data);

	if (!ctx) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);
	}

	const { recordType } = ctx;

	return (
		<WorkflowRuleFullPage
			ruleId={params.ruleId}
			organizationId={EMPTY_ORG_ID}
			lifecycleId={null}
			entityTypeId={params.recordTypeId}
			canEdit={canEditRule}
			canDelete={canDeleteRule}
			canArchive={canArchiveRule}
			entityTypeLabel={t("admin.recordTypes.table.title")}
			entityTypeName={getRecordTypeDisplayName(recordType)}
			activeTab={search.ruleTab}
			onTabChange={(ruleTab) => {
				void navigate({
					to: "/admin/configuration/recordTypes/$recordTypeId/full/rules/$ruleId",
					params: { recordTypeId: params.recordTypeId, ruleId: params.ruleId },
					search: { tab: "rules", mode: "view", presentation: "full", ruleTab },
				});
			}}
			onBackToRules={() => {
				void navigate({
					to: "/admin/configuration/recordTypes/$recordTypeId/full",
					params: { recordTypeId: params.recordTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onNavigateToEntityTypeList={() => {
				void navigate({ to: "/admin/configuration/recordTypes" });
			}}
			onNavigateToEntityTypeRules={() => {
				void navigate({
					to: "/admin/configuration/recordTypes/$recordTypeId/full",
					params: { recordTypeId: params.recordTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
			onAfterAction={() => {
				void navigate({
					to: "/admin/configuration/recordTypes/$recordTypeId/full",
					params: { recordTypeId: params.recordTypeId },
					search: { tab: "rules", mode: "view", presentation: "full" },
				});
			}}
		/>
	);
}
